import React, { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, ExternalLink, FileText, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  approveDocIntelligenceReview,
  discardDocIntelligenceUpload,
  getDocIntelligenceReview,
  getDocIntelligenceUploadPreviewUrl,
  patchDocIntelligenceReviewFields
} from '../../services/api/implementation/impl';
import {
  btnGhost,
  btnPrimary,
  btnSecondary,
  ConfidenceBar,
  DocIntelPanelHeader,
  DocIntelSkeleton
} from './DocIntelUi';
import {
  getUploadId,
  groupFieldsBySection,
  humanizeFieldName,
  isPreviewableImage
} from './docIntelUtils';
import DocIntelPdfRegionTagger from './DocIntelPdfRegionTagger';

const FIELD_CLASS_STYLES = {
  deal: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
  builder: 'bg-sky-50 text-sky-800 ring-sky-200',
  structural: 'bg-orange-50 text-orange-900 ring-orange-200',
  computed: 'bg-violet-50 text-violet-800 ring-violet-200',
  static: 'bg-slate-50 text-slate-600 ring-slate-200'
};

const FIELD_CLASS_LABELS = {
  deal: 'Deal',
  builder: 'Builder',
  structural: 'Land / legal',
  computed: 'Computed',
  static: 'Static'
};

function inferFieldClass(canonicalName = '') {
  const name = canonicalName.trim().toLowerCase();
  if (
    /^(applicant(?:\.\d+)?|unit|project)\./.test(name) ||
    /^(sale|booking|consideration|price|allottee)\./.test(name)
  ) {
    return 'deal';
  }
  if (/^company\./.test(name)) return 'builder';
  if (/^land\./.test(name) || /khasra|rera|jda|tncp|commencement/.test(name)) {
    return 'structural';
  }
  if (/computed|\.words$|\.total$|gst_amount/.test(name)) return 'computed';
  return 'static';
}

const DocIntelReviewPanel = ({
  upload,
  reviewSessionId,
  meta,
  onUpdated,
  revisitMode = false,
  onBackToTemplate
}) => {
  const [fields, setFields] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [layoutBlocks, setLayoutBlocks] = useState([]);
  const [isLoadingReview, setIsLoadingReview] = useState(true);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (!reviewSessionId) return;
    let cancelled = false;
    setIsLoadingReview(true);
    getDocIntelligenceReview(reviewSessionId)
      .then((session) => {
        if (cancelled) return;
        setFields(session.extracted_fields ?? []);
        setWarnings(session.warnings ?? []);
        setLayoutBlocks(session.layout_blocks ?? []);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || err?.message || 'Failed to load review');
      })
      .finally(() => {
        if (!cancelled) setIsLoadingReview(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reviewSessionId]);

  useEffect(() => {
    const uploadId = getUploadId(upload);
    if (!uploadId) return;
    let cancelled = false;
    setPreviewUrl(null);
    getDocIntelligenceUploadPreviewUrl(uploadId)
      .then((data) => {
        if (!cancelled) setPreviewUrl(data?.preview_url ?? null);
      })
      .catch(() => {
        if (!cancelled) setPreviewUrl(upload?.s3_url ?? null);
      });
    return () => {
      cancelled = true;
    };
  }, [upload]);

  const saveMutation = useMutation({
    mutationFn: () =>
      patchDocIntelligenceReviewFields(reviewSessionId, {
        extracted_fields: fields,
        document_type: upload?.document_kind || undefined
      }),
    onSuccess: () => {
      toast.success('Changes saved');
      onUpdated?.();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to save');
    }
  });

  const approveMutation = useMutation({
    mutationFn: async () => {
      await patchDocIntelligenceReviewFields(reviewSessionId, {
        extracted_fields: fields,
        document_type: upload?.document_kind || undefined
      });
      return approveDocIntelligenceReview(reviewSessionId);
    },
    onSuccess: () => {
      toast.success('Approved — ready to map');
      onUpdated?.();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to approve');
    }
  });

  const discardMutation = useMutation({
    mutationFn: () => discardDocIntelligenceUpload(getUploadId(upload)),
    onSuccess: () => {
      toast.success('Document discarded');
      onUpdated?.();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to discard');
    }
  });

  const updateField = (index, value) => {
    setFields((prev) => prev.map((field, i) => (i === index ? { ...field, value } : field)));
  };

  const toggleExcludeFromTemplate = (index) => {
    setFields((prev) =>
      prev.map((field, i) =>
        i === index
          ? { ...field, exclude_from_template: !field.exclude_from_template }
          : field
      )
    );
  };

  const updateFieldRegion = (canonicalName, region) => {
    setFields((prev) =>
      prev.map((field) =>
        field.canonical_name === canonicalName
          ? { ...field, ...(region ? { region } : { region: undefined }) }
          : field
      )
    );
  };

  const grouped = groupFieldsBySection(fields);
  const isBusy =
    saveMutation.isPending || approveMutation.isPending || discardMutation.isPending;
  const lowConfidenceCount = fields.filter((f) => (f.confidence ?? 1) < 0.75).length;
  const showImage = isPreviewableImage(upload?.mime_type, upload?.original_filename);
  const displayUrl = previewUrl || upload?.s3_url;
  const layoutSummary = layoutBlocks.reduce(
    (acc, block) => {
      const key = block?.type || 'body';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    },
    { header: 0, footer: 0, body: 0, table_candidate: 0 }
  );

  if (isLoadingReview) {
    return <DocIntelSkeleton rows={5} />;
  }

  return (
    <div className="flex min-h-0 flex-col">
      <DocIntelPanelHeader
        title={revisitMode ? 'Review fields' : meta?.title || 'Review extracted fields'}
        subtitle={meta?.subtitle}
        hint={
          revisitMode
            ? 'Field names stay as extracted — edit the value only, then return to the template.'
            : meta?.hint ||
              'Field names are already set. Enter or correct the value only for each field.'
        }
        actions={
          <>
            {revisitMode && onBackToTemplate ? (
              <button type="button" disabled={isBusy} onClick={onBackToTemplate} className={btnGhost}>
                Back to template
              </button>
            ) : (
              <button
                type="button"
                disabled={isBusy}
                onClick={() => discardMutation.mutate()}
                className={btnGhost}>
                Discard
              </button>
            )}
            {revisitMode ? (
              <button
                type="button"
                disabled={isBusy}
                onClick={() => saveMutation.mutate()}
                className={btnPrimary}>
                {saveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Save values
              </button>
            ) : (
              <button
                type="button"
                disabled={isBusy}
                onClick={() => approveMutation.mutate()}
                className={btnPrimary}>
                {approveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Approve &amp; continue
              </button>
            )}
          </>
        }
      />

      {(warnings.length > 0 || lowConfidenceCount > 0 || layoutBlocks.length > 0) && (
        <div className="mx-4 mt-4 flex items-start gap-2 rounded-xl border border-amber-200/90 bg-amber-50/90 px-3 py-2.5 text-sm text-amber-900 sm:mx-5">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="space-y-1">
            <p>
              {warnings.length > 0 && warnings.slice(0, 2).join(' · ')}
              {warnings.length > 0 && lowConfidenceCount > 0 && ' · '}
              {lowConfidenceCount > 0 &&
                `${lowConfidenceCount} field${lowConfidenceCount === 1 ? '' : 's'} below 75% confidence`}
            </p>
            {layoutBlocks.length > 0 && (
              <p className="text-xs text-amber-900/80">
                Layout:&nbsp;
                {layoutSummary.body} body · {layoutSummary.table_candidate} table ·{' '}
                {layoutSummary.header} header · {layoutSummary.footer} footer
                &nbsp;(letterhead/footer skipped when building the template)
              </p>
            )}
          </div>
        </div>
      )}

      <div className="grid flex-1 gap-4 p-4 sm:p-5 lg:grid-cols-2">
        <div className="flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-slate-50/40">
          <p className="border-b border-slate-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Document preview
          </p>
          <div className="flex min-h-[240px] flex-1 items-center justify-center p-3">
            {showImage && displayUrl ? (
              <img
                src={displayUrl}
                alt={upload.original_filename}
                className="max-h-[280px] max-w-full rounded-lg object-contain shadow-sm ring-1 ring-slate-200"
              />
            ) : displayUrl && upload?.mime_type === 'application/pdf' ? (
              <div className="h-[min(620px,65vh)] w-full overflow-hidden rounded-lg border border-slate-200 bg-white">
                <DocIntelPdfRegionTagger
                  url={displayUrl}
                  fields={fields}
                  onRegionChange={updateFieldRegion}
                />
              </div>
            ) : (
              <div className="flex flex-col items-center px-4 py-8 text-center">
                <FileText className="mb-3 h-10 w-10 text-slate-300" />
                <p className="text-sm font-medium text-slate-600">Preview in browser</p>
                {displayUrl && (
                  <a
                    href={displayUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-teal-800 hover:underline">
                    Open {upload.original_filename}
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="max-h-[min(420px,55vh)] overflow-y-auto rounded-xl border border-slate-200 bg-white">
          {Object.entries(grouped).map(([section, sectionFields]) => (
            <div key={section} className="border-b border-slate-100 p-4 last:border-b-0">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-[#2A565E]">
                {section}
              </p>
              <div className="space-y-4">
                {sectionFields.map((field) => {
                  const globalIndex = fields.findIndex(
                    (f) => f.canonical_name === field.canonical_name
                  );
                  const low = (field.confidence ?? 1) < 0.75;
                  const fieldClass = field.field_class || inferFieldClass(field.canonical_name);
                  const classStyle = FIELD_CLASS_STYLES[fieldClass] || FIELD_CLASS_STYLES.static;
                  const classLabel = FIELD_CLASS_LABELS[fieldClass] || fieldClass;
                  const excluded = Boolean(field.exclude_from_template);
                  return (
                    <div
                      key={field.canonical_name}
                      className={excluded ? 'opacity-60' : undefined}>
                      <div className="mb-1.5 flex flex-wrap items-center gap-2">
                        <label className="text-sm font-semibold text-slate-900">
                          {humanizeFieldName(field.canonical_name)}
                        </label>
                        <span
                          className={`rounded-full px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ring-1 ${classStyle}`}
                          title="How this field is used when building a reusable template">
                          {classLabel}
                        </span>
                        {field.region && (
                          <span className="rounded-full bg-teal-50 px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-teal-700 ring-1 ring-teal-200">
                            PDF p.{field.region.page}
                          </span>
                        )}
                        {(fieldClass === 'structural' || excluded) && (
                          <button
                            type="button"
                            onClick={() => toggleExcludeFromTemplate(globalIndex)}
                            className="ml-auto text-xs font-semibold text-slate-600 underline-offset-2 hover:text-slate-800 hover:underline">
                            {excluded ? 'Include in template' : 'Exclude from template'}
                          </button>
                        )}
                      </div>
                      <p className="mb-1 text-xs font-mono text-slate-500" title="Field key (already in template)">
                        {field.canonical_name}
                      </p>
                      <input
                        type="text"
                        value={field.value ?? ''}
                        onChange={(e) => updateField(globalIndex, e.target.value)}
                        placeholder="Enter value only…"
                        className={`w-full rounded-lg border px-3 py-2.5 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-600/20 ${
                          low
                            ? 'border-amber-300 bg-amber-50/30 focus:border-amber-400'
                            : 'border-slate-200 focus:border-teal-600'
                        }`}
                      />
                      {fieldClass === 'structural' && !excluded && (
                        <p className="mt-1 text-xs font-medium leading-snug text-orange-900">
                          Land/legal detail — blanked on promote so it doesn’t copy into other
                          projects.
                        </p>
                      )}
                      <ConfidenceBar confidence={field.confidence} className="mt-1.5" />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          {fields.length === 0 && (
            <p className="p-6 text-center text-sm text-slate-500">
              No fields extracted. Approve to continue or discard this upload.
            </p>
          )}
        </div>
      </div>

      <div className="flex justify-end border-t border-slate-100 px-4 py-3 sm:px-5">
        <button type="button" disabled={isBusy} onClick={() => saveMutation.mutate()} className={btnSecondary}>
          {saveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          Save edits
        </button>
      </div>
    </div>
  );
};

export default DocIntelReviewPanel;
